import { useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Switch,
} from '../components/ui'
import {
  createSite,
  getMe,
  listSites,
  updateSite,
  type CreateSiteInput,
} from '../api/endpoints'
import type { Site } from '../types'

/**
 * Parse a newline-or-comma-separated list of URLs into the canonical
 * scheme+host form the browser will actually send as Origin. The worker
 * runs the same normalisation on its end — we do it client-side too so
 * the operator sees the canonicalised value reflected back on save
 * (catches typos like trailing slashes before they're persisted).
 *
 * Anything we can't parse as http(s) is silently dropped — the worker
 * does the same, and the alternative (an error popup per bad line) gets
 * in the way when copy-pasting from a browser bar.
 */
function parseEmbedOrigins(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      try {
        const u = new URL(s)
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
        return u.origin
      } catch {
        return null
      }
    })
    .filter((s): s is string => s !== null)
}

/** One starter-chip prompt per line. Mirrors the worker's clamp (≤4 chips,
 *  ≤80 chars each, blanks dropped) so the dirty-check compares like-for-like
 *  against what the server will actually persist. */
function parseStarterPrompts(raw: string): string[] {
  return raw
    .split('\n')
    .map((s) => s.trim().slice(0, 80))
    .filter((s) => s.length > 0)
    .slice(0, 4)
}

/**
 * Sites management — operator-side onboarding wizard for new clients.
 * Per-site config edits (rename, change phone/email, scope lists, status)
 * happen in a follow-up "edit site" view; Phase 1 ships create + list +
 * embed snippet so an operator can take a brand-new client live in <5 min.
 */
export function SitesPage() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['sites'],
    queryFn: listSites,
  })
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe })
  // Site creation is superadmin-only — admins can tune their assigned
  // sites but shouldn't be able to onboard new clients of their own.
  const isSuperadmin = meQuery.data?.operator?.isSuperadmin === true
  const [showCreate, setShowCreate] = useState(false)

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (isError) {
    return (
      <Banner variant="error">
        Couldn’t load sites. <a onClick={() => refetch()}>Retry</a>
      </Banner>
    )
  }

  const sites = data?.sites ?? []

  return (
    <>
      <div
        style={{
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>Sites</h1>
          <p
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-sm)',
            }}
          >
            One row per client business. Add a site here to get a ready-to-paste
            embed snippet for that client’s website.
          </p>
        </div>
        {isSuperadmin && (
          <Button
            variant="primary"
            onClick={() => setShowCreate(true)}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Add a client
          </Button>
        )}
      </div>

      {isSuperadmin && showCreate && (
        <CreateSiteForm onCancel={() => setShowCreate(false)} />
      )}

      {sites.length === 0 ? (
        <EmptyState
          title="No sites yet"
          subtitle="Click Add a client above to register your first site."
        />
      ) : (
        <div>
          {sites.map((s) => (
            <SiteCard key={s.id} site={s} />
          ))}
        </div>
      )}
    </>
  )
}

function CreateSiteForm({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient()
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#1e3a8a')
  const [embedOriginsText, setEmbedOriginsText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [createdSite, setCreatedSite] = useState<Site | null>(null)

  const create = useMutation({
    mutationFn: (input: CreateSiteInput) => createSite(input),
    onSuccess: ({ site }) => {
      setError(null)
      setCreatedSite(site)
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : 'Site creation failed'
      setError(msg)
    },
  })

  function handleSubmit(e: Event) {
    e.preventDefault()
    if (!/^[a-z0-9][a-z0-9-]{1,30}$/.test(id)) {
      setError(
        'ID must be 2-31 chars, lowercase letters/numbers/dashes only'
      )
      return
    }
    if (!name.trim()) {
      setError('Display name required')
      return
    }
    const embedOrigins = parseEmbedOrigins(embedOriginsText)
    create.mutate({
      id,
      name: name.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      primaryColor: primaryColor || null,
      embedOrigins,
    })
  }

  if (createdSite) {
    const noOrigins = createdSite.embedOrigins.length === 0
    return (
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 6 }}>
          ✓ Created {createdSite.name}
        </h3>
        <p
          style={{
            color: 'var(--m-text-muted)',
            fontSize: 'var(--m-text-sm)',
            marginBottom: 12,
          }}
        >
          Paste this snippet on the client’s website (footer, before
          <code>&lt;/body&gt;</code>). Replace{' '}
          <code>WIDGET_SECRET</code> with the value from the
          shared-secrets password manager entry.
        </p>
        <pre
          style={{
            background: 'var(--m-surface-muted, #f4f4f7)',
            padding: 14,
            borderRadius: 8,
            fontSize: 12,
            overflowX: 'auto',
            marginBottom: 12,
          }}
        >
{`<script
  src="https://chat.momentum-wp.com/widget/concierge.js"
  data-site="${createdSite.id}"
  data-business-name="${createdSite.name}"
  data-primary-color="${createdSite.primaryColor ?? '#1e3a8a'}"
  data-greeting="Hi! How can I help you today?"
${createdSite.phone ? `  data-phone="${createdSite.phone}"\n` : ''}  data-widget-secret="WIDGET_SECRET"
  defer
></script>`}
        </pre>
        {noOrigins ? (
          <div class="banner banner-warning" style={{ marginBottom: 12 }}>
            <strong>No embed origins set.</strong> Browsers will block the
            widget's API responses on every site until you add at least
            one origin in the Edit panel. The bot won't reply to visitors
            in this state — it will look broken.
          </div>
        ) : (
          <p
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-xs)',
              marginBottom: 12,
            }}
          >
            Allowlisted origins:{' '}
            {createdSite.embedOrigins.map((o) => (
              <code style={{ marginRight: 6 }}>{o}</code>
            ))}
          </p>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Done
        </Button>
      </Card>
    )
  }

  return (
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 12 }}>Add a client</h3>
      <form onSubmit={handleSubmit}>
        <div class="field">
          <label class="field-label" for="site-id">
            Site ID (slug)
          </label>
          <input
            id="site-id"
            class="input"
            placeholder="e.g. acme-plumbing"
            value={id}
            onInput={(e) =>
              setId(
                (e.currentTarget as HTMLInputElement).value
                  .toLowerCase()
                  .replace(/[^a-z0-9-]/g, '')
              )
            }
            maxLength={31}
            required
          />
          <small
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-xs)',
            }}
          >
            Lowercase letters, numbers, dashes only. Goes into the embed
            snippet as <code>data-site</code>. Can’t be changed later.
          </small>
        </div>

        <div class="field">
          <label class="field-label" for="site-name">
            Business name
          </label>
          <input
            id="site-name"
            class="input"
            placeholder="e.g. Acme Plumbing"
            value={name}
            onInput={(e) =>
              setName((e.currentTarget as HTMLInputElement).value)
            }
            maxLength={100}
            required
          />
        </div>

        <div class="field">
          <label class="field-label" for="site-phone">
            Contact phone (optional)
          </label>
          <input
            id="site-phone"
            class="input"
            placeholder="01392 000000"
            value={phone}
            onInput={(e) =>
              setPhone((e.currentTarget as HTMLInputElement).value)
            }
          />
        </div>

        <div class="field">
          <label class="field-label" for="site-email">
            Contact email (optional)
          </label>
          <input
            id="site-email"
            class="input"
            type="email"
            placeholder="hello@example.com"
            value={email}
            onInput={(e) =>
              setEmail((e.currentTarget as HTMLInputElement).value)
            }
          />
        </div>

        <div class="field">
          <label class="field-label" for="site-color">
            Primary brand colour
          </label>
          <input
            id="site-color"
            class="input"
            type="color"
            value={primaryColor}
            onInput={(e) =>
              setPrimaryColor((e.currentTarget as HTMLInputElement).value)
            }
            style={{ maxWidth: 120, height: 40 }}
          />
        </div>

        <div class="field">
          <label class="field-label" for="site-embed-origins">
            Embed origins
          </label>
          <textarea
            id="site-embed-origins"
            class="input"
            placeholder={'https://example.co.uk\nhttps://www.example.co.uk'}
            value={embedOriginsText}
            rows={3}
            onInput={(e) =>
              setEmbedOriginsText(
                (e.currentTarget as HTMLTextAreaElement).value
              )
            }
            style={{ resize: 'vertical', fontFamily: 'ui-monospace, SF Mono, monospace', fontSize: 'var(--m-text-sm)' }}
          />
          <small
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-xs)',
            }}
          >
            One URL per line — the production domain(s) where the widget
            will be embedded. List <code>https://example.com</code> and{' '}
            <code>https://www.example.com</code> separately if both serve
            the site. Without at least one entry, the widget can't make
            API calls (browsers block the response). You can edit this
            later from the site's Edit panel.
          </small>
        </div>

        {error && (
          <div class="banner banner-error" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button
            type="submit"
            variant="primary"
            loading={create.isPending}
          >
            Create site
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  )
}

function SiteCard({ site }: { site: Site }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Card style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: site.primaryColor ?? '#9ca3af',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 'var(--m-weight-semibold)',
              fontSize: 'var(--m-text-base)',
            }}
          >
            {site.name}
          </div>
          <div
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-sm)',
              fontFamily: 'ui-monospace, SF Mono, monospace',
            }}
          >
            {site.id}
            {site.status !== 'active' && ` · ${site.status}`}
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Close' : 'Edit'}
        </Button>
      </div>
      {expanded && <SiteEditor site={site} />}
    </Card>
  )
}

/** Inline editor for the per-site fields it makes sense to tweak from this
 *  page. Today: the widget teaser + embed origin allowlist. Other fields
 *  (name, phone, colour) still go through the create wizard.
 *
 *  Embed origins are the security boundary for widget CORS — visitors on
 *  a non-allowlisted origin get blocked by their browser before the widget
 *  can show a response. So an outdated entry here looks like "the chat
 *  doesn't work" to a visitor with no error trail. Keep this editor
 *  prominent. */
function SiteEditor({ site }: { site: Site }) {
  const queryClient = useQueryClient()
  const [teaser, setTeaser] = useState(site.teaserMessage ?? '')
  const [originsText, setOriginsText] = useState(
    site.embedOrigins.join('\n')
  )
  const [chipsText, setChipsText] = useState(site.starterPrompts.join('\n'))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const mutation = useMutation({
    mutationFn: (patch: {
      teaserMessage?: string | null
      embedOrigins?: string[]
      starterPrompts?: string[]
      alertChannels?: { whatsapp: boolean; sms: boolean; call: boolean }
    }) => updateSite(site.id, patch),
    onSuccess: ({ site: updated }) => {
      setError(null)
      setSaved(true)
      // Reflect the canonicalised values the worker sent back — operator sees
      // the exact origins matched against the browser Origin header, and the
      // trimmed/capped chip list actually stored.
      setOriginsText(updated.embedOrigins.join('\n'))
      setChipsText(updated.starterPrompts.join('\n'))
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      setTimeout(() => setSaved(false), 2000)
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Save failed')
    },
  })

  function saveTeaser() {
    const trimmed = teaser.trim()
    mutation.mutate({ teaserMessage: trimmed === '' ? null : trimmed })
  }

  function saveOrigins() {
    mutation.mutate({ embedOrigins: parseEmbedOrigins(originsText) })
  }

  function saveChips() {
    mutation.mutate({ starterPrompts: parseStarterPrompts(chipsText) })
  }

  const dirty = (site.teaserMessage ?? '') !== teaser
  const originsDirty = site.embedOrigins.join('\n') !== originsText.trim()
  const chipsDirty =
    site.starterPrompts.join('\n') !== parseStarterPrompts(chipsText).join('\n')

  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 12,
        borderTop: '1px solid var(--m-border, #e5e7eb)',
      }}
    >
      {/* Escalation alert channels — push is always on; these are the rungs
          above it. SMS-only clients can switch WhatsApp off and never need
          the Meta template setup. */}
      <label
        style={{
          display: 'block',
          fontSize: 'var(--m-text-sm)',
          fontWeight: 'var(--m-weight-medium)',
          marginBottom: 6,
        }}
      >
        Escalation alert channels
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginBottom: 4 }}>
        {([
          ['whatsapp', 'WhatsApp (instant)'],
          ['sms', 'SMS (~2½ min)'],
          ['call', 'Voice call (~4 min)'],
        ] as const).map(([key, label]) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <Switch
              checked={site.alertChannels?.[key] !== false}
              onChange={(v) =>
                mutation.mutate({
                  alertChannels: {
                    whatsapp: site.alertChannels?.whatsapp !== false,
                    sms: site.alertChannels?.sms !== false,
                    call: site.alertChannels?.call !== false,
                    [key]: v,
                  },
                })
              }
              disabled={mutation.isPending}
              label={`Toggle ${label}`}
            />
            {label}
          </label>
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)', marginBottom: 16 }}>
        Push notifications always fire. Alerts go to each operator's number from
        their own Settings page.
      </div>

      <label
        style={{
          display: 'block',
          fontSize: 'var(--m-text-sm)',
          fontWeight: 'var(--m-weight-medium)',
          marginBottom: 6,
        }}
      >
        Embed origins
      </label>
      <p
        style={{
          fontSize: 'var(--m-text-xs)',
          color: 'var(--m-text-muted)',
          marginBottom: 8,
        }}
      >
        One URL per line — every origin the widget can be embedded on. The
        browser blocks the widget's API responses on any origin not listed
        here, so a stale entry shows up as "chat doesn't reply" to visitors
        on that site. List <code>https://example.com</code> and{' '}
        <code>https://www.example.com</code> separately if both serve the
        site.
      </p>
      <textarea
        value={originsText}
        rows={Math.max(3, site.embedOrigins.length + 1)}
        placeholder={'https://example.co.uk\nhttps://www.example.co.uk'}
        onInput={(e) =>
          setOriginsText((e.target as HTMLTextAreaElement).value)
        }
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 'var(--m-text-sm)',
          fontFamily: 'ui-monospace, SF Mono, monospace',
          border: '1px solid var(--m-border, #d1d5db)',
          borderRadius: 6,
          marginBottom: 8,
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
        <Button
          onClick={saveOrigins}
          disabled={!originsDirty || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save origins'}
        </Button>
        {site.embedOrigins.length === 0 && (
          <span
            style={{
              fontSize: 'var(--m-text-xs)',
              color: 'var(--m-danger, #b91c1c)',
            }}
          >
            ⚠ no origins set — widget will not work
          </span>
        )}
      </div>

      <label
        style={{
          display: 'block',
          fontSize: 'var(--m-text-sm)',
          fontWeight: 'var(--m-weight-medium)',
          marginBottom: 6,
        }}
      >
        Starter chips
      </label>
      <p
        style={{
          fontSize: 'var(--m-text-xs)',
          color: 'var(--m-text-muted)',
          marginBottom: 8,
        }}
      >
        One prompt per line, up to 4. Shown as tappable chips when a visitor
        opens the chat — tapping one sends it as their first message. Leave
        blank for none. Use the questions real visitors actually ask (e.g.{' '}
        <code>Do you deliver to my area?</code>).
      </p>
      <textarea
        value={chipsText}
        rows={Math.max(3, site.starterPrompts.length + 1)}
        placeholder={'Do you deliver to my area?\nWhat are your opening hours?\nHow much does X cost?'}
        onInput={(e) =>
          setChipsText((e.target as HTMLTextAreaElement).value)
        }
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 'var(--m-text-sm)',
          border: '1px solid var(--m-border, #d1d5db)',
          borderRadius: 6,
          marginBottom: 8,
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
        <Button
          onClick={saveChips}
          disabled={!chipsDirty || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save chips'}
        </Button>
      </div>

      <label
        style={{
          display: 'block',
          fontSize: 'var(--m-text-sm)',
          fontWeight: 'var(--m-weight-medium)',
          marginBottom: 6,
        }}
      >
        Teaser bubble
      </label>
      <p
        style={{
          fontSize: 'var(--m-text-xs)',
          color: 'var(--m-text-muted)',
          marginBottom: 8,
        }}
      >
        Pre-engagement message shown above the closed chat button after 5s.
        Leave blank to disable. Visitors who dismiss it don't see it again
        for 7 days. Avoid pricing or promises the bot can't back up.
      </p>
      <input
        type="text"
        value={teaser}
        maxLength={200}
        placeholder="Have a question? I can help."
        onInput={(e) => setTeaser((e.target as HTMLInputElement).value)}
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 'var(--m-text-sm)',
          border: '1px solid var(--m-border, #d1d5db)',
          borderRadius: 6,
          marginBottom: 8,
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          onClick={saveTeaser}
          disabled={!dirty || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save teaser'}
        </Button>
        {saved && (
          <span
            style={{
              fontSize: 'var(--m-text-xs)',
              color: 'var(--m-success, #15803d)',
            }}
          >
            ✓ Saved
          </span>
        )}
        {error && (
          <span
            style={{
              fontSize: 'var(--m-text-xs)',
              color: 'var(--m-danger, #b91c1c)',
            }}
          >
            {error}
          </span>
        )}
      </div>
    </div>
  )
}
